import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import Main from "./CommonModules/Main";
import Login from "./login/Login";
import EmailVerification from "./resetPassword/emailVerification";
import ForgotPassword from "./resetPassword/forgotPassword";
import ActionCardsForm from "./admin/ActionCardsForm";
import PublicRoute from "./routes/PublicRoute";
import PrivateRoute from "./routes/PrivateRoute";
import "./App.scss";
import { fetchCustomPoints } from "./store/reducers/badgesDetails";
import { getOrganizationById } from "./store/reducers/organizationSlice";

const App = () => {
  const dispatch = useDispatch();
  const { organizationDetails } = useSelector((state) => state.organization);
  const organizationId = localStorage.getItem("organisation_id");

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchCustomPoints(+organizationId));
      dispatch(getOrganizationById(+organizationId))
    }
  }, [organizationId, dispatch]);

  useEffect(() => {
    const root = document.documentElement;
    const isValidHex = (value) =>
      /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test((value || "").trim());
    const normalizeHex = (value) => {
      const hex = value.trim();
      if (hex.length === 4) {
        return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
      }
      return hex;
    };
    const toRgb = (hex) => {
      const normalized = normalizeHex(hex).replace("#", "");
      return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
      };
    };
    const mixWithWhite = (hex, whiteRatio) => {
      const { r, g, b } = toRgb(hex);
      const mixChannel = (channel) =>
        Math.round(channel * (1 - whiteRatio) + 255 * whiteRatio);
      const toHex = (channel) => channel.toString(16).padStart(2, "0");
      return `#${toHex(mixChannel(r))}${toHex(mixChannel(g))}${toHex(
        mixChannel(b),
      )}`;
    };
    const getLuminance = ({ r, g, b }) => {
      const convert = (channel) => {
        const value = channel / 255;
        return value <= 0.03928
          ? value / 12.92
          : ((value + 0.055) / 1.055) ** 2.4;
      };
      const rr = convert(r);
      const gg = convert(g);
      const bb = convert(b);
      return 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
    };
    const getContrastRatio = (firstHex, secondHex) => {
      const firstLum = getLuminance(toRgb(firstHex));
      const secondLum = getLuminance(toRgb(secondHex));
      const lighter = Math.max(firstLum, secondLum);
      const darker = Math.min(firstLum, secondLum);
      return (lighter + 0.05) / (darker + 0.05);
    };
    const getReadableTextColor = (backgroundHex, preferredTextHex) => {
      if (getContrastRatio(backgroundHex, preferredTextHex) >= 3) {
        return preferredTextHex;
      }
      const black = "#000000";
      const white = "#ffffff";
      return getContrastRatio(backgroundHex, black) >=
        getContrastRatio(backgroundHex, white)
        ? black
        : white;
    };

    const orgBackgroundColor = isValidHex(organizationDetails?.color)
      ? normalizeHex(organizationDetails.color.trim())
      : "#f6615f";
    const orgFontColor = isValidHex(organizationDetails?.font_color)
      ? normalizeHex(organizationDetails.font_color.trim())
      : "#fff";
    const activeBackgroundColor = "#ffffff";
    const activeTextColor = getReadableTextColor(
      activeBackgroundColor,
      orgFontColor,
    );
    const tint1 = mixWithWhite(orgBackgroundColor, 0.88); // close to #ffebee
    const tint2 = mixWithWhite(orgBackgroundColor, 0.95); // close to #fff7f6
    const tint3 = mixWithWhite(orgBackgroundColor, 0.9); // close to #ffefef
    const tint4 = mixWithWhite(orgBackgroundColor, 0.91); // close to #fff0f1

    root.style.setProperty("--org-bg-color", orgBackgroundColor);
    root.style.setProperty("--org-font-color", orgFontColor);
    root.style.setProperty("--org-active-bg-color", activeBackgroundColor);
    root.style.setProperty("--org-active-text-color", activeTextColor);
    root.style.setProperty("--org-bg-tint-1", tint1);
    root.style.setProperty("--org-bg-tint-2", tint2);
    root.style.setProperty("--org-bg-tint-3", tint3);
    root.style.setProperty("--org-bg-tint-4", tint4);
  }, [organizationDetails]);

  return (
    <Router>
      <Switch>

        <PublicRoute exact path="/login">
          <Login />
        </PublicRoute>
        <Route exact path="/form/:id">
          <ActionCardsForm />
        </Route>
        <PublicRoute exact path="/forgot-password">
          <EmailVerification />
        </PublicRoute>
        <Route exact path="/reset-password/:token">
          <ForgotPassword />
        </Route>
        <PublicRoute exact path="/callback">
          <h1>Callback</h1>
        </PublicRoute>
        <PrivateRoute path="/">
          <Main />
        </PrivateRoute>
      </Switch>
    </Router>
  );
};

export default App;
